import { useEffect, useState } from "react";
import { Loader2, Clock, LockIcon, Info } from "lucide-react";
import {
  ProgressData,
  PROGRESS_CONFIG,
  getProgressData,
  saveProgressData,
  calculateBackgroundProgress,
  initializeProgressData,
  isProcessingComplete,
} from "../utils/progressStorage";

interface ChatPlaceloaderProps {
  username: string;
  profilePic?: string;
}

const MESSAGES = [
  "Nuestro algoritmo detectó actividad automatizada en el perfil objetivo. Decodificando patrones...",
  "Seguimiento temporalmente interrumpido por límite de la API de Instagram. Reintentando en 5 minutos.",
  "Conectando con servidores espejo para eludir restricciones geográficas.",
  "Datos cifrados detectados. Ejecutando intento de descifrado nivel 1...",
  "Perfil bajo observación intensiva. Las imágenes archivadas pueden aparecer con retraso.",
  "Respaldo de stories en progreso. Videos y fotos serán mostrados según el orden de indexación.",
  "El historial del dispositivo parece haber sido alterado. Corrigiendo inconsistencias...",
  "Tu acceso fue añadido a la cola prioritaria. Mantén la app abierta para no perder el turno.",
  "Estamos extrayendo metadatos ocultos de los archivos multimedia del perfil objetivo.",
  "Nuestros bots fueron temporalmente bloqueados. Reiniciando vía túnel inverso...",
  "Actividad reciente sospechosa detectada en el perfil. Análisis más profundo iniciado.",
  "El objetivo puede haber activado medidas de privacidad avanzada. Ajustando protocolo...",
  "Perfiles con muchos seguidores suelen generar mayores retrasos de procesamiento.",
  "Estamos calibrando la IA de seguimiento basándonos en el último comportamiento del objetivo.",
  "Para seguridad de tu análisis, estamos ocultando tu IP con múltiples proxies.",
  "Escaneo programado cada 24h para nuevos eventos en el perfil observado.",
  "Alta demanda, por favor espera un poco más",
  "La persona limpió el historial del dispositivo, esto puede retrasar un poco el seguimiento",
  "Nuestros robots fueron bloqueados por Instagram, estamos trabajando para restaurar",
  "Nuestros robots harán escaneos cada 24h en este perfil, por favor espera la nueva ronda",
];

const ChatPlaceloader: React.FC<ChatPlaceloaderProps> = () => {
  const [progress, setProgress] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [processingComplete, setProcessingComplete] = useState(false);

  const isTyping = true;

  // Garantir que mensagens sejam exibidas de imediato
  const [displayedMessages, setDisplayedMessages] = useState<string[]>(() => {
    // Seleciona 3 mensagens aleatórias para mostrar de imediato
    const shuffled = [...MESSAGES].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 3);
  });

  // Initialize and manage progress
  useEffect(() => {
    const updateProgress = () => {
      const existingData = getProgressData();
      let currentData: ProgressData;

      if (existingData) {
        // Calculate background progress
        const newProgress = calculateBackgroundProgress(existingData);
        currentData = {
          ...existingData,
          progress: newProgress,
          lastUpdate: new Date().toISOString(),
        };
      } else {
        // Initialize new progress tracking
        currentData = initializeProgressData();
      }

      // Save updated data
      saveProgressData(currentData);

      // Update React state
      setProgress(currentData.progress);
      setLastUpdated(new Date(currentData.lastUpdate));
      setProcessingComplete(isProcessingComplete(currentData));
    };

    // Initial update
    updateProgress();

    // Set up interval for regular updates
    const interval = setInterval(() => {
      updateProgress();
    }, PROGRESS_CONFIG.UPDATE_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  // Adicionar mais mensagens a cada intervalo
  useEffect(() => {
    const interval = setInterval(() => {
      // Adiciona uma nova mensagem aleatória
      const randomMessage =
        MESSAGES[Math.floor(Math.random() * MESSAGES.length)];
      setDisplayedMessages((prev) => [...prev, randomMessage]);
    }, 40000); // A cada 10 segundos

    return () => clearInterval(interval);
  }, []);

  // Formato de tempo HH:MM
  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Larguras aleatórias para os balões de mensagem
  const getRandomWidth = () => {
    return 20 + Math.random() * 80;
  };

  // Horários aleatórios para as mensagens
  const getRandomTime = (): string => {
    const now = new Date();
    const minutesAgo = Math.floor(Math.random() * 60);
    now.setMinutes(now.getMinutes() - minutesAgo);
    return formatTime(now);
  };

  return (
    <div className="relative w-full mx-auto bg-neutral-900 rounded-lg overflow-hidden h-[500px] flex flex-col">
      {/* Blur overlay */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-md z-10 flex flex-col justify-center items-center">
        <div className="mb-4 relative w-14 h-14">
          <div className="absolute inset-0 rounded-full bg-neutral-800 animate-pulse"></div>
          <Loader2
            className="animate-spin absolute inset-0 m-auto text-blue-500"
            size={36}
          />
        </div>
        <div className="bg-neutral-800 rounded-lg p-4 max-w-[80%] mb-4 max-h-[60vh] overflow-y-auto">
          <div className="flex items-center space-x-2 mb-2">
            <Info size={16} className="text-blue-500" />
            <h3 className="text-white font-medium">
              Restaurando conversaciones y imágenes
            </h3>
          </div>

          <div className="mb-3">
            <div className="flex justify-between text-xs text-neutral-400 mb-1">
              <span>Progreso</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-neutral-700 rounded-full h-2 overflow-hidden">
              <div
                className="bg-gradient-to-r from-blue-600 to-blue-400 h-full transition-all duration-1000"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>

          <div className="flex items-center justify-between text-xs text-neutral-400 mb-3">
            <div className="flex items-center">
              <Clock size={12} className="mr-1" />
              <span>Última actualización: {formatTime(lastUpdated)}</span>
            </div>

            {processingComplete && (
              <span className="text-green-400">¡Procesamiento completo!</span>
            )}
          </div>

          {/* MENSAGENS DO SISTEMA - GARANTIDO QUE APARECERÁ */}
          <div className="mb-3 space-y-2">
            {displayedMessages.length > 0 && (
              <div className="bg-neutral-700 p-2 rounded text-xs text-white">
                {displayedMessages[displayedMessages.length - 1]}
              </div>
            )}
          </div>

          <div className="flex items-center text-xs text-neutral-500">
            <LockIcon size={12} className="mr-1" />
            <span>Seguimiento en segundo plano activo</span>
          </div>
        </div>

        <div className="text-center text-sm text-neutral-400 px-4">
          <p className="mb-2">
            {processingComplete
              ? "El seguimiento ha sido completado. El contenido está siendo revelado gradualmente."
              : "El contenido completo será revelado conforme avanza el seguimiento"}
          </p>
          <div className="flex justify-center">
            <span className="inline-flex items-center text-xs bg-neutral-800 px-2 py-1 rounded">
              <Info size={12} className="mr-1 text-blue-500" />
              Puedes cerrar esta página y volver después
            </span>
          </div>
        </div>
      </div>

      {/* Chat header */}
      <div className="border-b border-neutral-800 p-4 flex items-center">
        <div className="w-10 h-10 rounded-full bg-neutral-800 animate-pulse mr-3"></div>
        <div className="flex-1">
          <div className="h-4 w-24 bg-neutral-800 animate-pulse rounded"></div>
          <div className="h-3 w-16 bg-neutral-800 animate-pulse rounded mt-1"></div>
        </div>
      </div>

      {/* Chat messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Date separator */}
        <div className="flex justify-center">
          <div className="text-xs text-neutral-500 bg-neutral-900 px-2 py-1 rounded">
            Hoy
          </div>
        </div>

        {/* Generate random message bubbles */}
        {Array.from({ length: 8 }).map((_, index) => (
          <div
            key={index}
            className={`flex ${index % 2 === 0 ? "" : "justify-end"}`}
          >
            <div className="flex flex-col max-w-[70%]">
              <div
                className={`
                  rounded-2xl py-2 px-3
                  ${
                    index % 2 === 0
                      ? "bg-neutral-800 rounded-tl-none"
                      : "bg-neutral-700 rounded-tr-none"
                  }
                `}
                style={{ width: `${getRandomWidth()}%` }}
              >
                <div className="h-3 bg-neutral-700 animate-pulse rounded w-full"></div>
                {Math.random() > 0.5 && (
                  <div className="h-3 bg-neutral-700 animate-pulse rounded w-3/4 mt-1"></div>
                )}
              </div>
              <span className="text-xs text-neutral-500 mt-1">
                {getRandomTime()}
              </span>
            </div>
          </div>
        ))}

        {/* Typing indicator */}
        {isTyping && (
          <div className="flex">
            <div className="bg-neutral-800 rounded-2xl rounded-tl-none py-2 px-3">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-neutral-500 rounded-full animate-bounce"></div>
                <div
                  className="w-2 h-2 bg-neutral-500 rounded-full animate-bounce"
                  style={{ animationDelay: "0.2s" }}
                ></div>
                <div
                  className="w-2 h-2 bg-neutral-500 rounded-full animate-bounce"
                  style={{ animationDelay: "0.4s" }}
                ></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Chat input */}
      <div className="border-t border-neutral-800 p-3">
        <div className="flex items-center bg-neutral-800 rounded-full px-4 py-2">
          <div className="h-4 w-full bg-neutral-700 animate-pulse rounded"></div>
        </div>
      </div>
    </div>
  );
};

export default ChatPlaceloader;
